import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Eye, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Clients = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    registration_number: '',
    address: '',
    email: ''
  });

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeader() });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/clients`, formData, { headers: getAuthHeader() });
      toast.success('Client added successfully');
      setIsOpen(false);
      fetchClients();
      setFormData({ name: '', registration_number: '', address: '', email: '' });
    } catch (error) {
      toast.error('Failed to add client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (name) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await axios.delete(`${API}/clients/${name}`, { headers: getAuthHeader() });
      toast.success('Client deleted');
      fetchClients();
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('clients')}</h2>
        {canWrite(user?.role) && <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2 bg-accent hover:bg-accent/90" data-testid="add-client-btn">
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addClient')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Company Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="client-name-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('registrationNumber')}</Label>
                <Input value={formData.registration_number} onChange={(e) => setFormData({...formData, registration_number: e.target.value})} data-testid="client-registration-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Address</Label>
                <Input value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} data-testid="client-address-input" />
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>E-mail</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="client-email-input" />
              </div>
              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="client-submit-btn" disabled={isSubmitting}>
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {clients.map((client, idx) => (
          <Card key={idx} className="p-4 border border-border hover:border-slate-300 transition-colors" data-testid={`client-card-${idx}`}>
            <div className="space-y-2">
              <h3 className="font-semibold text-lg">{client.name}</h3>
              <p className="text-sm text-muted-foreground">{client.registration_number}</p>
              <p className="text-sm text-muted-foreground">{client.email}</p>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" onClick={() => navigate(`/clients-suppliers/client/${client.id}`)} data-testid={`view-client-${idx}`}>
                  <Eye className="w-4 h-4 me-2" />
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('view')}</span>
                </Button>
                {user?.role === 'admin' && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(client.name)} data-testid={`delete-client-${idx}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
      {clients.length === 0 && (
        <div className="text-center py-12 text-muted-foreground" data-testid="empty-clients">
          <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noClientsYet')}</p>
        </div>
      )}
    </Card>
  );
};